import { useCallback, useRef } from 'react';

/**
 * Only the newest request may write the list. Changing a filter, the page or the month
 * while a slower earlier request is still in flight let that earlier response land last
 * and show the old filter's rows under the new filter.
 *
 * `begin()` starts a request and returns `isLatest()`, true until another one begins.
 */
export const latestGate = () => {
  let seq = 0;
  return () => {
    const mine = ++seq;
    return () => mine === seq;
  };
};

/** {@link latestGate} kept for the life of a component. */
export const useLatestRequest = (): (() => () => boolean) => {
  const gate = useRef<ReturnType<typeof latestGate> | null>(null);
  if (!gate.current) gate.current = latestGate();
  return useCallback(() => gate.current!(), []);
};

/** After a filter shrinks a list, a page past the end shows nothing: step back to the last one. */
export const clampPage = (page: number, totalPages: number): number =>
  totalPages > 0 && page > totalPages ? totalPages : page;
